import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, forwardRef, inject, input } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { type ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms'
import { type MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field'
import { MatSelectModule } from '@angular/material/select'
import { InsuranceProviderLookupsService } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'
import { orderBy } from 'lodash'
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search'
import { combineLatest, map, shareReplay, startWith } from 'rxjs'

/**
 * Reusable, searchable multi-select for insurance carriers.
 *
 * Self-contained (renders its own `<mat-form-field>`) and integrates with
 * reactive forms via `ControlValueAccessor`, so it drops into any form with
 * `formControlName`. Its value is an array of carrier lookup ids — the same
 * `carrierLookupId` stored on appointment insurances.
 */
@Component({
  selector: 'mmx-carrier-multi-select',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './carrier-multi-select.component.html',
  styleUrls: ['./carrier-multi-select.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    TranslateModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CarrierMultiSelectComponent),
      multi: true,
    },
  ],
})
export class CarrierMultiSelectComponent implements ControlValueAccessor {
  private readonly lookupsService = inject(InsuranceProviderLookupsService)

  readonly selectControl = new FormControl<string[]>([], { nonNullable: true })
  readonly filterControl = new FormControl<string>('', { nonNullable: true })
  readonly labelKey = input('common.insurance')
  readonly appearance = input<MatFormFieldAppearance>('fill')

  private readonly carriers$ = this.lookupsService.all$.pipe(
    map((lookups) => orderBy(lookups, 'clinicCarrierName')),
    shareReplay(1),
  )

  readonly filteredCarriers$ = combineLatest([
    this.carriers$,
    this.filterControl.valueChanges.pipe(startWith('')),
  ]).pipe(
    map(([lookups, term]) => {
      const query = term.toLowerCase().trim()
      return query
        ? lookups.filter((lookup) => (lookup.clinicCarrierName ?? '').toLowerCase().includes(query))
        : lookups
    }),
  )

  private onChange: (value: string[]) => void = () => { /* registered by forms */ }
  onTouched: () => void = () => { /* registered by forms */ }

  constructor() {
    this.selectControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.onChange(value))
  }

  writeValue(value: string[] | null): void {
    this.selectControl.setValue(value ?? [], { emitEvent: false })
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.selectControl.disable({ emitEvent: false })
    } else {
      this.selectControl.enable({ emitEvent: false })
    }
  }
}
